import { useEffect, useMemo, useRef, useState } from "react";

import {
  Button,
  Search,
  Selector,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  type SelectorOption,
} from "../../components/ui";
import type { AgentStoredDocument, XLMModel } from "../api/e2e";
import { formatXLMNumber } from "../lib/xlmDisplay";
import { xlmAssumptionsReviewRows } from "../lib/xlmReviews";
import {
  modelReferenceAvailable,
  type ModelReference,
} from "../lib/modelReferences";
import { useAgentDocumentOpener } from "./AgentDocumentActionContext";
import AgentReviewModelLink from "./AgentReviewModelLink";
import AgentSummaryDocumentPreview from "./AgentSummaryDocumentPreview";

const HEADERS = [
  "Assumption line item",
  "Period",
  "Assumption value",
  "LY actual",
  "3-yr avg",
  "Segment",
  "Impact of +1pp on segment revenue ($mm)",
  "Impact of +1pp on segment revenue (%)",
  "Assumption Rationale",
  "Document reference",
] as const;

const TEXT_COLUMN_MIN_WIDTHS: Partial<Record<number, number>> = {
  0: 280,
  1: 180,
  5: 260,
  8: 560,
  9: 320,
};

const ALL_YEARS = "all" as const;
type AssumptionYearFilter = typeof ALL_YEARS | `${number}`;
const ALL_QUARTERS = "all" as const;
type AssumptionQuarterFilter = typeof ALL_QUARTERS | `${number}`;
const ALL_SEGMENTS = "all" as const;
type AssumptionSegmentFilter = typeof ALL_SEGMENTS | `segment:${string}`;

const ALL_YEARS_OPTION: SelectorOption<AssumptionYearFilter> = {
  value: ALL_YEARS,
  label: "All years",
};
const ALL_QUARTERS_OPTION: SelectorOption<AssumptionQuarterFilter> = {
  value: ALL_QUARTERS,
  label: "All quarters",
};
const ALL_SEGMENTS_OPTION: SelectorOption<AssumptionSegmentFilter> = {
  value: ALL_SEGMENTS,
  label: "All segments",
};

function formattedValue(value: number | null, unit: string | null): string {
  return value === null ? "—" : formatXLMNumber(value, unit);
}

export default function AgentAssumptionsReviewPanel({
  companyId,
  model,
  documents,
  activeRecordId = null,
  onOpenModelReference,
}: {
  companyId: string;
  model: XLMModel;
  documents: AgentStoredDocument[];
  activeRecordId?: string | null;
  onOpenModelReference: (reference: ModelReference) => void;
}) {
  const {
    closePreview,
    openDocument,
    openingDocumentId: hostOpeningDocumentId,
    previewDocument,
  } = useAgentDocumentOpener();
  const rows = useMemo(() => xlmAssumptionsReviewRows(model), [model]);
  const [year, setYear] = useState<AssumptionYearFilter>(ALL_YEARS);
  const [quarter, setQuarter] = useState<AssumptionQuarterFilter>(ALL_QUARTERS);
  const [segment, setSegment] = useState<AssumptionSegmentFilter>(ALL_SEGMENTS);
  const [search, setSearch] = useState("");
  const yearOptions: readonly SelectorOption<AssumptionYearFilter>[] = [
    ALL_YEARS_OPTION,
    ...[...new Set(rows.map((row) => row.year))]
      .sort((left, right) => right - left)
      .map((rowYear) => ({
        value: `${rowYear}` as AssumptionYearFilter,
        label: `FY${rowYear}`,
      })),
  ];
  const quarterOptions: readonly SelectorOption<AssumptionQuarterFilter>[] = [
    ALL_QUARTERS_OPTION,
    ...[...new Set(rows.flatMap((row) => row.quarter ?? []))]
      .sort((left, right) => left - right)
      .map((rowQuarter) => ({
        value: `${rowQuarter}` as AssumptionQuarterFilter,
        label: `Q${rowQuarter}`,
      })),
  ];
  const segmentOptions: readonly SelectorOption<AssumptionSegmentFilter>[] = [
    ALL_SEGMENTS_OPTION,
    ...[...new Set(rows.flatMap((row) => row.segment ?? []))]
      .sort((left, right) => left.localeCompare(right))
      .map((rowSegment) => ({
        value: `segment:${rowSegment}` as AssumptionSegmentFilter,
        label: rowSegment,
      })),
  ];
  const query = search.trim().toLowerCase();
  const filteredRows = rows.filter(
    (row) =>
      (year === ALL_YEARS || row.year === Number(year)) &&
      (quarter === ALL_QUARTERS || row.quarter === Number(quarter)) &&
      (segment === ALL_SEGMENTS ||
        row.segment === segment.slice("segment:".length)) &&
      (!query || row.item.toLowerCase().includes(query)),
  );
  const documentsById = new Map(
    documents.map((document) => [document.id, document]),
  );
  const section = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!activeRecordId) return;
    section.current
      ?.querySelector('[data-review-active="true"]')
      ?.scrollIntoView?.({ block: "center", inline: "nearest" });
  }, [activeRecordId]);

  if (rows.length === 0) {
    return (
      <section className="agent-assumptions-review agent-assumptions-review--empty">
        This model has no non-zero commented red forecast inputs in Revenue
        Build to review.
      </section>
    );
  }

  return (
    <section ref={section} className="agent-assumptions-review">
      <div className="agent-assumptions-review__toolbar">
        <Selector
          className="agent-assumptions-review__year"
          options={yearOptions}
          value={year}
          onValueChange={setYear}
          ariaLabel="Filter by year"
        />
        <Selector
          className="agent-assumptions-review__quarter"
          options={quarterOptions}
          value={quarter}
          onValueChange={setQuarter}
          ariaLabel="Filter by quarter"
        />
        <Selector
          className="agent-assumptions-review__segment"
          options={segmentOptions}
          value={segment}
          onValueChange={setSegment}
          ariaLabel="Filter by segment"
        />
        <Search
          className="agent-assumptions-review__search"
          size="sm"
          value={search}
          onValueChange={setSearch}
          placeholder="Search"
          aria-label="Search line items"
        />
      </div>

      <TableContainer className="agent-assumptions-review__table-container">
        <Table className="agent-assumptions-review__table">
          <TableHead>
            <TableRow>
              {HEADERS.map((header, index) => (
                <TableHeader
                  key={header}
                  className={
                    index === 8
                      ? "agent-assumptions-review__rationale"
                      : undefined
                  }
                  style={{ minWidth: TEXT_COLUMN_MIN_WIDTHS[index] }}
                >
                  {header}
                </TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row) => {
              const document = row.document
                ? documentsById.get(row.document)
                : undefined;
              const reference = row.reference;
              const referenceAvailable =
                reference !== null && modelReferenceAvailable(model, reference);
              const assumptionValue = formattedValue(row.assumption, row.unit);
              const cells = [
                referenceAvailable && reference ? (
                  <AgentReviewModelLink
                    reference={reference}
                    title={`Open ${row.item}, ${row.period} in Model`}
                    onOpenModelReference={onOpenModelReference}
                  >
                    {row.item}
                  </AgentReviewModelLink>
                ) : (
                  row.item
                ),
                row.period,
                referenceAvailable && reference ? (
                  <AgentReviewModelLink
                    value
                    reference={reference}
                    title={`Open assumption value ${assumptionValue} for ${row.item}, ${row.period} in Model`}
                    onOpenModelReference={onOpenModelReference}
                  >
                    {assumptionValue}
                  </AgentReviewModelLink>
                ) : (
                  assumptionValue
                ),
                formattedValue(row.actual, row.unit),
                formattedValue(row.average, row.unit),
                row.segment ?? "—",
                formattedValue(row.impact, "currency_mm"),
                formattedValue(row.impactRate, "percent"),
                row.reason,
                document ? (
                  <Button
                    type="button"
                    tone="quiet"
                    size="xs"
                    loading={hostOpeningDocumentId === document.id}
                    onClick={() => openDocument(document)}
                  >
                    {document.name}
                  </Button>
                ) : (
                  "—"
                ),
              ];
              const active = row.id === activeRecordId;
              return (
                <TableRow
                  key={row.id}
                  className={active ? "agent-review-row--active" : undefined}
                  aria-current={active ? "true" : undefined}
                  data-review-active={active ? "true" : undefined}
                >
                  {cells.map((cell, index) => (
                    <TableCell
                      key={HEADERS[index]}
                      className={
                        index === 8
                          ? "agent-assumptions-review__rationale"
                          : undefined
                      }
                    >
                      {cell}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {previewDocument && (
        <AgentSummaryDocumentPreview
          companyId={companyId}
          document={previewDocument}
          onClose={closePreview}
        />
      )}
    </section>
  );
}
